import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import { useEffect, useState } from 'react';
import type { CampaignData } from 'services/api/adminCampaigns.api';
import { NAVY, RADIUS_COIN, YELLOW } from 'styles/tokens';

import CampaignConfiguration, { type CampaignUploadResult } from './components/CampaignConfiguration';
import CampaignDetails from './components/CampaignDetails';
import CampaignsList from './components/CampaignsList';
import CampaignUploadSummaryDialog from './components/CampaignUploadSummaryDialog';
import SelectBusinessUnitAndTemplate from './components/SelectBusinessUnitAndTemplate';
import UploadCampaignFile from './components/UploadCampaignFile';

type ViewMode = 'list' | 'create' | 'details' | 'no-bu';

const AdminCampaigns = () => {
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const queryParams = new URLSearchParams(window.location.search);
  const queryBusinessUnitId = queryParams.get('businessUnitId');
  const parsedBusinessUnitId = parseInt(queryBusinessUnitId || '', 10);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [campaignData, setCampaignData] = useState<CampaignData>({
    businessUnitId: Number.isNaN(parsedBusinessUnitId) ? null : parsedBusinessUnitId,
    selectedTemplate: null,
    templateBodyParameters: [],
    campaignName: '',
    messagesToSendPerPeriod: 150,
    timePeriodInMinutes: 20,
    startDate: new Date(Date.now() + 15 * 60 * 1000), // 15 minutes from now
    startHour: 9,
    endHour: 18,
    sendOnWeekend: false,
    totalFileRows: null,
    uploadedFile: null,
    mappedHeaders: null,
    separator: null,
  });
  const [successResult, setSuccessResult] = useState<CampaignUploadResult | null>(null);

  const steps = ['Seleccionar Plantilla', 'Subir Archivo de Clientes', 'Configurar Campaña'];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setCampaignData({
      businessUnitId: null,
      selectedTemplate: null,
      templateBodyParameters: [],
      campaignName: '',
      messagesToSendPerPeriod: 150,
      timePeriodInMinutes: 20,
      startDate: new Date(Date.now() + 15 * 60 * 1000), // 15 minutes from now
      startHour: 9,
      endHour: 18,
      sendOnWeekend: false,
      totalFileRows: null,
      uploadedFile: null,
      mappedHeaders: null,
      separator: null,
    });
  };

  useEffect(() => {
    if (viewMode === 'no-bu' && selectedBusinessUnitId && selectedBusinessUnitId > 0) {
      setViewMode('create');
      handleReset();
    }
  }, [selectedBusinessUnitId]);

  const handleSuccess = (result: CampaignUploadResult) => {
    setSuccessResult(result);
    handleReset();
    setViewMode('list');
  };

  const handleViewCampaign = (campaignId: number) => {
    setSelectedCampaignId(campaignId);
    setViewMode('details');
  };

  const handleCreateCampaign = () => {
    if (!selectedBusinessUnitId || selectedBusinessUnitId <= 0) {
      setViewMode('no-bu');
      return;
    }
    setViewMode('create');
    handleReset();
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedCampaignId(null);
  };

  return (
    <Box sx={{ px: { xs: 2, lg: 4 }, py: 3 }}>
      {successResult && <CampaignUploadSummaryDialog result={successResult} onClose={() => setSuccessResult(null)} />}
      <Box>
        {viewMode === 'list' && (
          <CampaignsList onViewCampaign={handleViewCampaign} onCreateCampaign={handleCreateCampaign} />
        )}

        {viewMode === 'no-bu' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 'calc(100vh - 120px)',
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 600, fontSize: '1.125rem', color: NAVY, mb: 1 }}
            >
              Selecciona una unidad de negocio
            </Typography>
            <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.95rem', color: `${NAVY}99` }}>
              Escoge una unidad de negocio en la barra lateral para crear una campaña.
            </Typography>
          </Box>
        )}

        {viewMode === 'details' && selectedCampaignId && (
          <CampaignDetails campaignId={selectedCampaignId} onBack={handleBackToList} />
        )}

        {viewMode === 'create' && (
          <>
            {/* Create flow header */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToList}
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  color: NAVY,
                  bgcolor: 'rgba(21, 24, 58, 0.06)',
                  borderRadius: RADIUS_COIN,
                  px: 2.5,
                  '&:hover': { bgcolor: 'rgba(21, 24, 58, 0.11)' },
                }}
              >
                Volver
              </Button>
            </Box>

            {/* Stepper */}
            <Box sx={{ mb: 3 }}>
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  '& .MuiStepIcon-root': { color: 'rgba(21, 24, 58, 0.2)' },
                  '& .MuiStepIcon-root.Mui-active': { color: YELLOW },
                  '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': { fill: NAVY },
                  '& .MuiStepIcon-root.Mui-completed': { color: NAVY },
                  '& .MuiStepLabel-label': { fontFamily: 'Jost, sans-serif', color: NAVY, opacity: 0.6 },
                  '& .MuiStepLabel-label.Mui-active': { opacity: 1, fontWeight: 700 },
                  '& .MuiStepLabel-label.Mui-completed': { opacity: 0.8 },
                  '& .MuiStepConnector-line': { borderColor: 'rgba(21, 24, 58, 0.15)' },
                }}
              >
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>

            <Box>
              {activeStep === 0 && (
                <SelectBusinessUnitAndTemplate
                  campaignData={campaignData}
                  setCampaignData={setCampaignData}
                  onNext={handleNext}
                />
              )}
              {activeStep === 1 && campaignData.businessUnitId && campaignData.selectedTemplate && (
                <UploadCampaignFile
                  campaignData={campaignData}
                  setCampaignData={setCampaignData}
                  onNext={handleNext}
                  onBack={handleBack}
                />
              )}
              {activeStep === 2 && (
                <CampaignConfiguration
                  campaignData={campaignData}
                  setCampaignData={setCampaignData}
                  onSuccess={handleSuccess}
                  onBack={handleBack}
                />
              )}
            </Box>

            {activeStep === steps.length && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
                <Button
                  onClick={handleReset}
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 700,
                    bgcolor: YELLOW,
                    color: NAVY,
                    borderRadius: RADIUS_COIN,
                    px: 3,
                    '&:hover': { bgcolor: '#e6c009' },
                  }}
                >
                  Crear otra campaña
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminCampaigns;
